class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener(
            "keyup",
            (evt) => evt.code === "Escape" && this.close(),
        );
        this.querySelector("#CartDrawer-Overlay").addEventListener(
            "click",
            this.close.bind(this),
        );
        this.setHeaderCartIconAccessibility();
    }

    connectedCallback() {
        this.addEventListener("click", this.handleUpsellClick.bind(this));
        this.addEventListener("click", this.handleUpsellTabClick.bind(this));
        this.addEventListener(
            "click",
            this.handleUpsellCarouselClick.bind(this),
        );
        this.addEventListener(
            "scroll",
            this.handleUpsellCarouselScroll.bind(this),
            true,
        );
        this.updateUpsellCarousels();
    }

    handleUpsellTabClick(event) {
        const pill = event.target.closest("[data-upsell-tab]");
        if (!pill) return;

        const tabs = pill.closest("[data-upsell-tabs]");
        if (!tabs) return;

        event.preventDefault();

        const index = pill.dataset.upsellTab;

        tabs.querySelectorAll("[data-upsell-tab]").forEach((tab) => {
            const isActive = tab.dataset.upsellTab === index;
            tab.classList.toggle("is-active", isActive);
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        tabs.querySelectorAll("[data-upsell-panel]").forEach((panel) => {
            panel.classList.toggle(
                "is-active",
                panel.dataset.upsellPanel === index,
            );
        });
    }

    handleUpsellClick(event) {
        const button = event.target.closest("[data-cart-drawer-upsell-add]");
        if (!button) return;

        event.preventDefault();

        const variantId = button.dataset.variantId;
        const errorElement = button
            .closest(".cart-drawer__upsell")
            ?.querySelector("[data-cart-drawer-upsell-error]");

        if (!variantId) return;

        button.disabled = true;
        button.classList.add("loading");
        if (errorElement) errorElement.textContent = "";

        const body = JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
        });

        fetch(routes.cart_add_url, { ...fetchConfig("javascript"), body })
            .then((response) => response.json())
            .then((parsedState) => {
                if (parsedState.status) {
                    throw new Error(
                        parsedState.description || window.cartStrings.error,
                    );
                }
                this.renderContents(parsedState);
            })
            .catch((error) => {
                console.error(error);
                if (errorElement) {
                    errorElement.textContent =
                        error.message || window.cartStrings.error;
                }
            })
            .finally(() => {
                button.disabled = false;
                button.classList.remove("loading");
            });
    }

    handleUpsellCarouselClick(event) {
        const previousButton = event.target.closest(
            "[data-cart-drawer-upsell-prev]",
        );
        const nextButton = event.target.closest(
            "[data-cart-drawer-upsell-next]",
        );
        const dotButton = event.target.closest("[data-cart-drawer-upsell-dot]");

        if (!previousButton && !nextButton && !dotButton) return;

        const carousel = event.target.closest(
            "[data-cart-drawer-upsell-carousel]",
        );
        const track = carousel?.querySelector(
            "[data-cart-drawer-upsell-track]",
        );
        if (!carousel || !track) return;

        event.preventDefault();

        const slides = Array.from(
            track.querySelectorAll("[data-cart-drawer-upsell-slide]"),
        );
        if (!slides.length) return;

        const currentIndex = this.getUpsellCarouselIndex(track, slides);
        let nextIndex = currentIndex;

        if (previousButton) nextIndex = Math.max(currentIndex - 1, 0);
        if (nextButton)
            nextIndex = Math.min(currentIndex + 1, slides.length - 1);
        if (dotButton) nextIndex = Number(dotButton.dataset.slideIndex || 0);

        slides[nextIndex]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
        });
        this.updateUpsellCarousel(carousel, nextIndex);
    }

    handleUpsellCarouselScroll(event) {
        const track = event.target.closest?.("[data-cart-drawer-upsell-track]");
        if (!track) return;

        const carousel = track.closest("[data-cart-drawer-upsell-carousel]");
        if (!carousel) return;

        window.requestAnimationFrame(() => {
            const slides = Array.from(
                track.querySelectorAll("[data-cart-drawer-upsell-slide]"),
            );
            this.updateUpsellCarousel(
                carousel,
                this.getUpsellCarouselIndex(track, slides),
            );
        });
    }

    getUpsellCarouselIndex(track, slides) {
        if (!slides.length) return 0;

        const slideWidth = slides[0].getBoundingClientRect().width;
        if (!slideWidth) return 0;

        return Math.max(
            0,
            Math.min(
                slides.length - 1,
                Math.round(track.scrollLeft / slideWidth),
            ),
        );
    }

    updateUpsellCarousels() {
        this.querySelectorAll("[data-cart-drawer-upsell-carousel]").forEach(
            (carousel) => {
                this.updateUpsellCarousel(carousel);
            },
        );
    }

    updateUpsellCarousel(carousel, activeIndex = 0) {
        const track = carousel.querySelector("[data-cart-drawer-upsell-track]");
        const slides = Array.from(
            carousel.querySelectorAll("[data-cart-drawer-upsell-slide]"),
        );
        const dots = Array.from(
            carousel.parentElement?.querySelectorAll(
                "[data-cart-drawer-upsell-dot]",
            ) || [],
        );
        const previousButton = carousel.querySelector(
            "[data-cart-drawer-upsell-prev]",
        );
        const nextButton = carousel.querySelector(
            "[data-cart-drawer-upsell-next]",
        );

        if (track && slides.length) {
            activeIndex = this.getUpsellCarouselIndex(track, slides);
        }

        if (previousButton)
            previousButton.disabled = activeIndex <= 0 || slides.length <= 1;
        if (nextButton)
            nextButton.disabled =
                activeIndex >= slides.length - 1 || slides.length <= 1;

        dots.forEach((dot, index) => {
            dot.classList.toggle("is-active", index === activeIndex);
        });
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        if (!cartLink || this.isCartPage()) return;

        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");
        cartLink.addEventListener("click", (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener("keydown", (event) => {
            if (event.code.toUpperCase() === "SPACE") {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    isCartPage() {
        const cartPath = new URL(routes.cart_url, window.location.origin)
            .pathname;
        return window.location.pathname === cartPath;
    }

    open(triggeredBy) {
        if (!this.querySelector(".cart__items--disabled")) {
            this.classList.remove("is-updating");
        }
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
            this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add("animate", "active");
        });

        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains(
                    "is-empty",
                )
                    ? this.querySelector(".drawer__inner-empty")
                    : document.getElementById("CartDrawer");
                const focusElement =
                    this.querySelector(".drawer__inner") ||
                    this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true },
        );

        document.body.classList.add("overflow-hidden");
    }

    close() {
        this.classList.remove("active", "is-updating");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");

        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute(
                "aria-controls",
                cartDrawerNote.nextElementSibling.id,
            );
        }

        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute(
                "aria-expanded",
                !event.currentTarget.closest("details").hasAttribute("open"),
            );
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    renderContents(parsedState, { open = true } = {}) {
        if (this.isCartPage()) {
            window.location.reload();
            return;
        }

        this.classList.remove("is-updating");
        this.productId = parsedState.id;
        this.renderSections(parsedState.sections);

        if (this.querySelector("cart-drawer-items")) {
            this.classList.remove("is-empty");
            this.querySelector(".drawer__inner")?.classList.remove("is-empty");
        }

        setTimeout(() => {
            this.classList.remove("is-updating");
            this.querySelector("#CartDrawer-Overlay")?.addEventListener(
                "click",
                this.close.bind(this),
            );
            this.updateUpsellCarousels();
            if (open) this.open();
        });
    }

    renderSections(sections) {
        if (!sections) return;

        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            if (!sectionElement || !sections[section.id]) return;

            sectionElement.innerHTML = this.getSectionInnerHTML(
                sections[section.id],
                section.selector,
            );

            if (section.id === "cart-drawer") {
                this.activateInsertedScripts(sectionElement);
            }
        });
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    activateInsertedScripts(container) {
        container.querySelectorAll("script").forEach((script) => {
            const executableScript = document.createElement("script");

            Array.from(script.attributes).forEach((attribute) => {
                executableScript.setAttribute(attribute.name, attribute.value);
            });

            executableScript.textContent = script.textContent;
            script.replaceWith(executableScript);
        });
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-drawer",
                selector: "#CartDrawer",
            },
            {
                id: "cart-icon-bubble",
            },
        ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }

    async refresh({ open = false, triggeredBy = null } = {}) {
        if (triggeredBy) this.setActiveElement(triggeredBy);

        const sectionIds = [
            ...new Set(this.getSectionsToRender().map((section) => section.id)),
        ].join(",");

        try {
            const response = await fetch(
                `${routes.cart_url}?sections=${sectionIds}`,
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.renderContents({ sections: await response.json() }, { open });
        } catch (error) {
            console.error(error);
            if (!open) throw error;
            this.open(triggeredBy);
        }
    }
}

customElements.define("cart-drawer", CartDrawer);

window.openCartDrawer = function (triggeredBy, refresh = true) {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || typeof drawer.open !== "function") return false;
    if (refresh && typeof drawer.refresh === "function") {
        drawer.refresh({ open: true, triggeredBy });
    } else {
        drawer.open(triggeredBy);
    }
    return true;
};

class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: "CartDrawer",
                section: "cart-drawer",
                selector: ".drawer__inner",
                preserveScripts: true,
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section",
            },
        ];
    }
}

customElements.define("cart-drawer-items", CartDrawerItems);
